import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useIsomorphicLayoutEffect, useResizeObserver } from 'usehooks-ts';

// Widths resolve from rem, so an exactly-fitting table still reports a fraction
// of a pixel of overflow.
const PAN_EPSILON_PX = 1;

// Breathing room either side of a control panned into view.
const FOCUS_MARGIN_PX = 16;

// A trackpad swipe arrives as a stream of wheel events; a gap this long ends one.
const GESTURE_IDLE_MS = 150;

// How far an event has to lean vertical to be read as a new gesture rather than
// noise within a sideways one. A swipe still in motion always carries deltaX.
const VERTICAL_DOMINANCE = 3;

// Accumulated rather than judged per event: one stray sideways delta is harmless,
// and canceling it would swallow the page's scroll.
const BACK_NAV_BUDGET_PX = 40;

// Firefox on Windows and Linux reports a wheel notch as whole lines, so an
// unscaled delta of 3 would pan 3px. 40px/line follows `normalize-wheel`.
const DELTA_MODE_LINE = 1;
const DELTA_MODE_PAGE = 2;
const WHEEL_LINE_HEIGHT_PX = 40;

/**
 * The width a table's canvases stretch to, below which content spills and it pans.
 * Fixed columns resolve from rem and the rest from px, so the units stay apart
 * until CSS adds them.
 */
export const getCanvasWidth = (
  columns: { fixedWidth?: number; width: number }[],
): string => {
  let flexPx = 0;
  let fixedRem = 0;

  columns.forEach(({ fixedWidth, width }) => {
    if (fixedWidth === undefined) flexPx += width;
    else fixedRem += fixedWidth;
  });

  return `calc(${flexPx}px + ${fixedRem}rem)`;
};

export interface UseHorizontalTableScrollOptions {
  /**
   * Canvas holding the body rowgroup, panned across its viewport.
   */
  bodyCanvasRef: RefObject<HTMLElement>;
  /**
   * Window onto the body canvas, clipped to the table's width.
   */
  bodyViewportRef: RefObject<HTMLElement>;
  /**
   * Canvas holding the header rowgroup, panned in step with the body.
   */
  headerCanvasRef: RefObject<HTMLElement>;
  /**
   * Changes whenever the published canvas width changes, forcing a re-measure.
   */
  measureKey?: string;
  /**
   * Spacer inside the proxy, sized to encode the pan range.
   */
  proxyGhostRef: RefObject<HTMLElement>;
  /**
   * The floating scrollbar — the table's only scroll container, and the source
   * of truth for how far it is panned.
   */
  proxyRef: RefObject<HTMLElement>;
  /**
   * Table root that carries the overflow and pan-position attributes the
   * stylesheets gate on.
   */
  rootRef: RefObject<HTMLElement>;
}

/**
 * Pans a table sideways by translating its header and body canvases together,
 * driven by the floating scrollbar, and publishes the overflow and pan-position
 * attributes the stylesheets gate the scroll chrome on. See panning.mdx on why the
 * table pans rather than scrolls.
 */
export const useHorizontalTableScroll = ({
  bodyCanvasRef,
  bodyViewportRef,
  headerCanvasRef,
  measureKey,
  proxyGhostRef,
  proxyRef,
  rootRef,
}: UseHorizontalTableScrollOptions): void => {
  const maxPanRef = useRef(0);
  const wheelBoundRef = useRef(false);
  const gestureRef = useRef<{ at: number; axis: 'x' | 'y'; leakedX: number }>({
    at: 0,
    axis: 'y',
    leakedX: 0,
  });

  const applyPan = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;

    const panPx = proxyRef.current?.scrollLeft ?? 0;

    // Empty rather than `translateX(0)`, so an unpanned canvas is no containing
    // block for fixed descendants — and a sub-pixel rest counts as unpanned.
    const transform = panPx > PAN_EPSILON_PX ? `translateX(${-panPx}px)` : '';
    [bodyCanvasRef.current, headerCanvasRef.current].forEach(canvas => {
      if (canvas) canvas.style.transform = transform;
    });

    root.toggleAttribute('data-table-scroll-left', panPx > PAN_EPSILON_PX);
    root.toggleAttribute(
      'data-table-scroll-right',
      panPx < maxPanRef.current - PAN_EPSILON_PX,
    );
  }, [bodyCanvasRef, headerCanvasRef, proxyRef, rootRef]);

  // Nothing here is a scroll container, so the wheel is ours to handle — and every
  // sideways event needs canceling, or the remainder becomes a back-navigation.
  const onWheel = useCallback(
    (event: WheelEvent) => {
      const proxy = proxyRef.current;
      if (!proxy || maxPanRef.current <= PAN_EPSILON_PX) return;
      if (event.deltaX === 0 && event.deltaY === 0) return;

      const isShiftPan = event.shiftKey && event.deltaX === 0;
      const gesture = gestureRef.current;

      // Locked per gesture, so a mostly-vertical swipe with a little sideways
      // jitter cannot pan.
      if (event.timeStamp - gesture.at > GESTURE_IDLE_MS) {
        gesture.axis =
          isShiftPan || Math.abs(event.deltaX) > Math.abs(event.deltaY)
            ? 'x'
            : 'y';
        gesture.leakedX = 0;
      } else if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        // Raising the lock only ever cancels more.
        gesture.axis = 'x';
      }
      gesture.at = event.timeStamp;

      // Momentum outlives the finger, so a spent pan can still hold the lock.
      const isVerticalDominant =
        !isShiftPan &&
        Math.abs(event.deltaY) > Math.abs(event.deltaX) * VERTICAL_DOMINANCE;

      // Canceling is decided separately from panning, but never for a decisively
      // vertical event: that would take the page's scroll with it. See panning.mdx.
      const sideways = isShiftPan ? event.deltaY : event.deltaX;
      if (gesture.axis !== 'x' || isVerticalDominant) {
        if (!isVerticalDominant) {
          gesture.leakedX += sideways;
          if (Math.abs(gesture.leakedX) >= BACK_NAV_BUDGET_PX) {
            event.preventDefault();
          }
        }
        return;
      }

      // A page is the viewport, which the proxy spans, rather than a line count.
      const scale =
        event.deltaMode === DELTA_MODE_LINE
          ? WHEEL_LINE_HEIGHT_PX
          : event.deltaMode === DELTA_MODE_PAGE
            ? proxy.clientWidth
            : 1;

      proxy.scrollLeft += sideways * scale;
      event.preventDefault();
    },
    [proxyRef],
  );

  // Bound only while there is something to pan: passivity is fixed at registration,
  // so a permanent non-passive listener would hold up scroll on every table.
  const bindWheel = useCallback(
    (pannable: boolean) => {
      const root = rootRef.current;
      if (!root || pannable === wheelBoundRef.current) return;
      wheelBoundRef.current = pannable;

      // Captured at the root so no part of a gesture escapes through the toolbar,
      // footer, or the scrollbar itself.
      if (pannable) {
        root.addEventListener('wheel', onWheel, {
          capture: true,
          passive: false,
        });
      } else {
        root.removeEventListener('wheel', onWheel, { capture: true });
      }
    },
    [onWheel, rootRef],
  );

  const measure = useCallback(() => {
    const canvas = bodyCanvasRef.current;
    const proxy = proxyRef.current;
    const root = rootRef.current;
    const viewport = bodyViewportRef.current;
    if (!canvas || !proxy || !root || !viewport) return;

    // The wider of the two: a consumer can set its own header min-width, and a pan
    // range short of it would clip the header against a body that fits.
    const canvasWidth = Math.max(
      canvas.getBoundingClientRect().width,
      headerCanvasRef.current?.getBoundingClientRect().width ?? 0,
    );

    const maxPanPx = Math.max(0, canvasWidth - viewport.clientWidth);
    maxPanRef.current = maxPanPx;

    const pannable = maxPanPx > PAN_EPSILON_PX;
    root.toggleAttribute('data-table-hscroll', pannable);
    bindWheel(pannable);

    // The ghost encodes the pan range, not the canvas width, so the proxy's own
    // maximum scroll is that range whatever its width.
    const ghost = proxyGhostRef.current;
    if (ghost) ghost.style.width = `${proxy.clientWidth + maxPanPx}px`;

    applyPan();
  }, [
    applyPan,
    bindWheel,
    bodyCanvasRef,
    bodyViewportRef,
    headerCanvasRef,
    proxyGhostRef,
    proxyRef,
    rootRef,
  ]);

  useIsomorphicLayoutEffect(() => {
    measure();
  }, [measure, measureKey]);

  // Catches the viewport and the context panel's animated reflow alike, with no
  // transition listener.
  useResizeObserver({ onResize: measure, ref: bodyViewportRef });

  useEffect(() => {
    const proxy = proxyRef.current;
    if (!proxy) return undefined;

    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        applyPan();
      });
    };

    proxy.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      if (frame) cancelAnimationFrame(frame);
      proxy.removeEventListener('scroll', onScroll);
    };
  }, [applyPan, proxyRef]);

  useEffect(() => () => bindWheel(false), [bindWheel]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    // The browser brings a focused control into view by scrolling its nearest
    // scroll container, and the table no longer has one, so pan to it by hand.
    let pointerHeld = false;
    const onPointerDown = () => {
      pointerHeld = true;
    };
    const onPointerRelease = () => {
      pointerHeld = false;
    };

    const onFocusIn = (event: FocusEvent) => {
      const proxy = proxyRef.current;
      const viewport = bodyViewportRef.current;
      if (!proxy || !viewport || maxPanRef.current <= PAN_EPSILON_PX) return;
      if (!(event.target instanceof HTMLElement)) return;

      // Panning a control out from under a held pointer retargets its click onto
      // the row, so wait for the release; focus with nothing held is the keyboard's.
      if (pointerHeld) return;

      const viewportRect = viewport.getBoundingClientRect();
      const targetRect = event.target.getBoundingClientRect();
      const pastRight = targetRect.right - viewportRect.right;
      const pastLeft = viewportRect.left - targetRect.left;

      if (pastRight > 0) proxy.scrollLeft += pastRight + FOCUS_MARGIN_PX;
      else if (pastLeft > 0) proxy.scrollLeft -= pastLeft + FOCUS_MARGIN_PX;
    };

    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('pointerdown', onPointerDown, { capture: true });
    // Capture, so a release swallowed further down the tree still clears the latch
    // and leaves focus panning working.
    window.addEventListener('pointerup', onPointerRelease, { capture: true });
    window.addEventListener('pointercancel', onPointerRelease, {
      capture: true,
    });
    // A right-click opens the context menu and never sends the matching pointerup,
    // which would strand the latch and take focus panning down with it for good.
    window.addEventListener('contextmenu', onPointerRelease, { capture: true });

    return () => {
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('pointerup', onPointerRelease, true);
      window.removeEventListener('pointercancel', onPointerRelease, true);
      window.removeEventListener('contextmenu', onPointerRelease, true);
    };
  }, [bodyViewportRef, proxyRef, rootRef]);
};

export default useHorizontalTableScroll;
